import { Color } from "@mui/material"
import { CourseInfo } from "../../../types/gqlTypes/graphql"
import {
  blueGrey,
  cyan,
  deepOrange,
  deepPurple,
  lime,
  pink,
  teal
} from "./palette"

export type Colors = {
  color: Color
  primary: string
  secondary: string
  light: string
  initial: string
  initialHover: string
  alternate: string
  alternateHover: string
  text: string
}

type CategoryColors = {
  [category: string]: Colors
}

export type Categories =
  | "math"
  | "geometry"
  | "science"
  | "biology"
  | "english"
  | "writing"
  | "reading"
  | "history"
  | "social"
  | "overall"

const math: Colors = {
  color: pink,
  primary: pink[500],
  secondary: pink[600],
  light: pink[100],
  initial: pink[500],
  initialHover: pink[600],
  alternate: pink[300],
  alternateHover: pink[400],
  text: pink[900]
}

const science: Colors = {
  color: lime,
  primary: lime[700],
  secondary: lime[500],
  light: lime[100],
  initial: lime[500],
  initialHover: lime[600],
  alternate: lime[300],
  alternateHover: lime[400],
  text: lime[900]
}

const english: Colors = {
  color: deepPurple,
  primary: deepPurple[500],
  secondary: deepPurple[400],
  light: deepPurple[50],
  initial: deepPurple[500],
  initialHover: deepPurple[600],
  alternate: deepPurple[300],
  alternateHover: deepPurple[400],
  text: deepPurple[900]
}

const reading: Colors = {
  color: deepOrange,
  primary: deepOrange[500],
  secondary: deepOrange[600],
  light: deepOrange[100],
  initial: deepOrange[500],
  initialHover: deepOrange[600],
  alternate: deepOrange[300],
  alternateHover: deepOrange[400],
  text: deepOrange[900]
}

const history: Colors = {
  color: cyan,
  primary: cyan[600],
  secondary: cyan[500],
  light: cyan[100],
  initial: cyan[500],
  initialHover: cyan[600],
  alternate: cyan[300],
  alternateHover: cyan[400],
  text: cyan[900]
}

const overall: Colors = {
  color: teal,
  primary: teal[600],
  secondary: teal[500],
  light: teal[100],
  initial: teal[500],
  initialHover: teal[600],
  alternate: teal[300],
  alternateHover: teal[400],
  text: teal[900]
}

export const categoryColors: CategoryColors = {
  math,
  geometry: math,
  science,
  biology: science,
  english,
  reading,
  writing: reading,
  history,
  social: history,
  overall
}

export const getCategoryColors = (category: string): Colors => {
  const findCategory = Object.keys(categoryColors).find(key =>
    category?.includes(key)
  )

  return (
    categoryColors[findCategory] ?? {
      color: blueGrey,
      primary: blueGrey[600],
      secondary: blueGrey[500],
      light: blueGrey[50],
      initial: blueGrey[500],
      initialHover: blueGrey[600],
      alternate: blueGrey[300],
      alternateHover: blueGrey[400],
      text: blueGrey[900]
    }
  )
}

// Resolves a BE subject name (e.g. "Reading and Writing", "US History") to the
// category key (e.g. "reading", "history") that getCategoryColors expects.
// Lowercasing the subject directly works by coincidence for ACT but breaks for
// any subject whose display name doesn't substring-match a category key.
export const getCourseCategoryForSubject = (
  programSubjects: CourseInfo[] | null | undefined,
  subjectName: string | null | undefined
): string | undefined => {
  if (!subjectName || !programSubjects?.length) return undefined
  return (
    programSubjects.find(s => s?.name === subjectName)?.courseCategories?.at(-1) ??
    undefined
  )
}

// Convenience: subject name straight to the full Colors object. Use when the
// caller needs colors immediately. Prefer getCourseCategoryForSubject when the
// key string has to be plumbed through child component props.
export const getColorsForSubject = (
  programSubjects: CourseInfo[] | null | undefined,
  subjectName: string | null | undefined
): Colors =>
  getCategoryColors(
    getCourseCategoryForSubject(programSubjects, subjectName) ?? ""
  )
